"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { createClient } from "@/lib/supabase";
import type { Project } from "@/types/database";
import { StaggerContainer, StaggerItem } from "@/components/AnimateSection";

export function FeaturedProjects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from("projects")
      .select("*")
      .eq("featured", true)
      .order("sort_order", { ascending: true })
      .limit(6)
      .then(({ data }) => {
        setProjects(data ?? []);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="clay-card h-64 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <p className="text-dev-text-secondary">
        Chưa có dự án nổi bật. Thêm projects trong Admin.
      </p>
    );
  }

  return (
    <StaggerContainer className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {projects.map((project) => (
        <StaggerItem key={project.id} as="div">
          <Link
            href={`/projects/${encodeURIComponent(project.slug)}/`}
            className="clay-card group block overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-[1.02]"
          >
          {project.image_url && (
            <div className="relative aspect-video overflow-hidden rounded-t-[20px] bg-dev-bg-medium">
              <Image
                src={project.image_url}
                alt={project.title}
                width={800}
                height={450}
                unoptimized
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dev-bg-dark/80 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </div>
          )}
          <div className="p-5">
            <h3 className="font-semibold text-dev-text-primary transition-colors group-hover:text-dev-cyan">
              {project.title}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm text-dev-text-secondary">
              {project.description}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(project.tech_stack ?? []).slice(0, 3).map((tech) => (
                <span
                  key={tech}
                  className="rounded-lg bg-dev-bg-medium/50 px-2 py-1 text-xs text-dev-cyan border border-dev-blue/30"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </Link>
        </StaggerItem>
      ))}
    </StaggerContainer>
  );
}
